import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useColorMode } from '@chakra-ui/react'
import type { ViewMode } from '@/components/editor'
import { getShareMeta, ApiError } from '@/lib/api/shares'
import { parseShareIdParam } from '@/lib/utils/share-url'
import { getStoredApiKey } from '@/lib/storage/local-storage'
import {
  validateShareAccess,
  determineDecryptionPath,
  decryptShareContent,
  consumeShareQuietly,
} from '@/lib/shares/load'
import type { ShareDetails } from '@/types/share'

type ViewState = 'loading' | 'passcode' | 'decrypting' | 'viewing' | 'error' | 'expired' | 'consumed' | 'burn-confirm'

/**
 * Manages the full share loading lifecycle: metadata fetching, decryption strategy
 * selection (public / API key / passcode), burn-after-read confirmation, and state
 * transitions for the ViewSharePage.
 */
export function useShareLoading() {
  const { shareId: shareIdParam } = useParams<{ shareId: string }>()
  const shareId = shareIdParam ? parseShareIdParam(shareIdParam) : undefined
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setColorMode } = useColorMode()
  const [state, setState] = useState<ViewState>('loading')
  const [metadata, setMetadata] = useState<ShareDetails | null>(null)
  const [decryptedContent, setDecryptedContent] = useState<string | null>(null)
  const [error, setError] = useState<string>('')

  // Stores the action to run after burn-after-read confirmation
  const pendingBurnAction = useRef<(() => Promise<void>) | null>(null)

  const fragmentPasscode = location.hash ? location.hash.slice(1) : null

  // URL params for theme and mode
  const themeParam = searchParams.get('theme')
  const modeParam = searchParams.get('mode')
  const defaultViewMode: ViewMode = modeParam && ['split', 'editor', 'preview'].includes(modeParam)
    ? modeParam as ViewMode
    : 'preview'

  // Apply theme from URL param
  useEffect(() => {
    if (themeParam === 'dark' || themeParam === 'light') {
      setColorMode(themeParam)
    }
  }, [themeParam, setColorMode])

  // Update page title with filename when viewing
  useEffect(() => {
    if (state === 'viewing' && metadata?.filename) {
      document.title = metadata.filename
    }
    return () => {
      document.title = 'myrkup'
    }
  }, [state, metadata?.filename])

  // --- Decryption + consume helper ---

  const decryptAndShow = async (meta: ShareDetails, path: ReturnType<typeof determineDecryptionPath>) => {
    if (!shareId) return

    setState('decrypting')
    try {
      const content = await decryptShareContent(shareId, meta, path)
      setDecryptedContent(content)
      setState('viewing')

      if (meta.burnAfterRead) {
        await consumeShareQuietly(shareId)
        toast.warning('This share has been consumed and will not be accessible again.')
      }
    } catch (err) {
      // API key decryption failed — fall back to passcode
      if (path.type === 'api-key') {
        console.error('API key decryption failed, falling back to passcode:', err)
        if (fragmentPasscode) {
          await decryptAndShow(meta, { type: 'passcode', passcode: fragmentPasscode })
        } else {
          setState('passcode')
        }
        return
      }

      if (path.type === 'passcode') {
        console.error('Decryption failed:', err)
        toast.error('Incorrect passcode. Please try again.')
        setState('passcode')
        return
      }

      console.error('Failed to load share:', err)
      setError('Failed to load share content.')
      setState('error')
    }
  }

  // --- Manual passcode entry handler (used by PasscodePrompt) ---

  const handleDecrypt = async (passcode: string, meta?: ShareDetails) => {
    const shareMeta = meta || metadata
    if (!shareMeta) return
    await decryptAndShow(shareMeta, { type: 'passcode', passcode })
  }

  // --- Load metadata and determine decryption path ---

  useEffect(() => {
    loadShareMetadata()
  }, [shareId])

  const loadShareMetadata = async () => {
    if (!shareId) return

    try {
      const storedApiKey = getStoredApiKey()
      const meta = await getShareMeta(shareId, storedApiKey ? { apiKey: storedApiKey } : undefined)
      setMetadata(meta)

      const access = validateShareAccess(meta)
      if (!access.valid) {
        setState(access.reason)
        return
      }

      const path = determineDecryptionPath(meta, storedApiKey, fragmentPasscode)

      const proceedWithDecryption = async () => {
        if (path.type === 'needs-passcode') {
          setState('passcode')
        } else {
          await decryptAndShow(meta, path)
        }
      }

      if (access.needsBurnConfirm) {
        pendingBurnAction.current = proceedWithDecryption
        setState('burn-confirm')
      } else {
        await proceedWithDecryption()
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError('Share not found or has been revoked.')
        } else if (err.status === 410) {
          setState('consumed')
          return
        } else {
          setError(err.message)
        }
      } else {
        setError('Failed to load share.')
      }
      setState('error')
    }
  }

  // --- Burn confirmation + navigation ---

  const handleBurnConfirm = useCallback(async () => {
    const action = pendingBurnAction.current
    pendingBurnAction.current = null
    if (action) {
      await action()
    }
  }, [])

  const handleBurnCancel = useCallback(() => {
    pendingBurnAction.current = null
    navigate('/')
  }, [navigate])

  const handleEdit = useCallback(() => {
    navigate('/editor', {
      state: {
        prefillContent: decryptedContent,
        prefillFilename: metadata?.filename
      }
    })
  }, [decryptedContent, metadata?.filename, navigate])

  return {
    state,
    metadata,
    decryptedContent,
    error,
    shareId,
    defaultViewMode,
    handleDecrypt,
    handleBurnConfirm,
    handleBurnCancel,
    handleEdit,
  }
}
